import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, currentWeek, CHART_COLORS } from "@/lib/format";
import { StatusPill } from "@/components/StatusPill";
import ExpenseDialog from "@/components/ExpenseDialog";
import { WeekSelect } from "@/components/WeekSelect";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { ArrowLeft, ArrowRight, Loader2, FileText, FileDown, Paperclip, Plus, Lock } from "lucide-react";

export default function HomeDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { settings } = useAuth();
  const cur = settings.currency || "£";
  const [week, setWeek] = useState(params.get("week") || currentWeek());
  const [r, setR] = useState(null);
  const [selected, setSelected] = useState(null);
  const [dlg, setDlg] = useState(false);

  const load = () => { setR(null); api.get(`/reports/weekly?site_id=${id}&week=${week}`).then((x) => setR(x.data)); };
  useEffect(() => { load(); }, [id, week]);

  const openRow = (e) => { setSelected(e); setDlg(true); };
  const token = localStorage.getItem("hs_token");
  const exp = (fmt) => window.open(`${API}/export/weekly?site_id=${id}&week=${week}&fmt=${fmt}&auth=${token}`, "_blank");

  if (!r) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const pct = r.allocation ? Math.round((r.spent / r.allocation) * 100) : 0;
  const color = pct < (settings.thresholds?.amber ?? 70) ? "#3E8755" : pct <= (settings.thresholds?.red ?? 90) ? "#F29C38" : "#D94E4E";

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <button data-testid="home-back" onClick={() => nav(-1)} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors duration-200 mb-1"><ArrowLeft className="h-4 w-4" /> Back</button>
          <h1 className="font-head text-2xl lg:text-3xl font-semibold text-slate-900" data-testid="home-name">{r.site_name}</h1>
          <p className="text-slate-500 text-sm mt-1">{r.week_label} · <span className="capitalize inline-flex items-center gap-1">{r.status === "reconciled" && <Lock className="h-3 w-3" />}{r.status}</span></p>
        </div>
        <div className="flex items-center gap-3">
          <WeekSelect value={week} onChange={setWeek} />
          <Button data-testid="home-add-expense" onClick={() => nav("/add")} className="bg-[#0F172A] hover:bg-[#1E293B]"><Plus className="h-4 w-4 mr-1.5" /> Add Expense</Button>
        </div>
      </div>

      {/* Allocated -> Spent -> Remaining flow */}
      <Card className="border-border p-6" data-testid="home-flow">
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr_auto_1fr] items-center gap-4 sm:gap-2">
          <Flow label="Allocated" value={money(r.allocation, cur)} sub="Weekly budget" tone="text-slate-900" />
          <ArrowRight className="hidden sm:block h-6 w-6 text-slate-300 mx-auto" />
          <Flow label="Approved Spend" value={money(r.spent, cur)} sub={`${pct}% used`} tone="text-slate-900" />
          <ArrowRight className="hidden sm:block h-6 w-6 text-slate-300 mx-auto" />
          <Flow label="Remaining" value={money(r.remaining, cur)} sub={r.remaining < 0 ? "Over budget" : "Left to spend"} tone={r.remaining < 0 ? "text-red-600" : "text-emerald-700"} />
        </div>
        <div className="mt-5 h-2.5 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(pct, 100)}%`, background: color }} />
        </div>
      </Card>

      <div className="flex flex-wrap gap-4">
        <MiniStat label="Transactions" value={r.transactions.length} />
        <MiniStat label="Receipts" value={r.receipts} sub={`${r.receipt_rate}% complete`} />
        <MiniStat label="Outstanding Queries" value={r.outstanding_queries} tone={r.outstanding_queries ? "text-amber-600" : ""} />
        <MiniStat label="Adjustments" value={r.adjustments?.length || 0} />
        <div className="ml-auto flex items-end gap-2">
          <Button size="sm" variant="outline" data-testid="home-export-pdf" onClick={() => exp("pdf")}><FileText className="h-4 w-4 mr-1.5" /> PDF</Button>
          <Button size="sm" variant="outline" data-testid="home-export-csv" onClick={() => exp("csv")}><FileDown className="h-4 w-4 mr-1.5" /> CSV</Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="border-border p-5">
          <h3 className="font-head font-medium text-slate-800 mb-3">Where it was spent</h3>
          {r.category_breakdown.length === 0 ? <p className="text-sm text-slate-400 py-8 text-center">No approved spend yet</p> : (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart><Pie data={r.category_breakdown} dataKey="amount" nameKey="category" cx="50%" cy="50%" outerRadius={80}>
                  {r.category_breakdown.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie><Tooltip formatter={(v) => money(v, cur)} /></PieChart>
              </ResponsiveContainer>
              <div className="mt-3 space-y-1.5">
                {r.category_breakdown.map((c, i) => (
                  <div key={c.category} className="flex items-center gap-2 text-sm">
                    <span className="h-2.5 w-2.5 rounded-sm" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                    <span className="flex-1 text-slate-700">{c.category}</span>
                    <span className="font-mono tabular-nums font-medium">{money(c.amount, cur)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>

        <Card className="lg:col-span-2 border-border overflow-hidden">
          <div className="px-5 py-4 border-b border-border"><h3 className="font-head font-medium text-slate-800">Transactions &amp; receipts</h3></div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Merchant</TableHead><TableHead>Category</TableHead><TableHead>Staff</TableHead>
                <TableHead className="text-right">Amount</TableHead><TableHead className="text-center">Receipt</TableHead><TableHead>Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {r.transactions.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">No transactions this week.</TableCell></TableRow>}
                {r.transactions.map((t) => (
                  <TableRow key={t.id} data-testid={`home-txn-${t.ref}`} className="cursor-pointer hover:bg-slate-50" onClick={() => openRow(t)}>
                    <TableCell className="font-medium text-slate-800">{t.merchant}<div className="text-xs text-slate-400">{t.purchase_date}</div></TableCell>
                    <TableCell className="text-slate-600">{t.category}</TableCell>
                    <TableCell className="text-slate-600">{t.staff_name}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums font-medium">{money(t.amount, cur)}</TableCell>
                    <TableCell className="text-center">{t.has_receipt ? <Paperclip className="h-4 w-4 text-emerald-600 mx-auto" /> : <span className="text-xs text-red-500">Missing</span>}</TableCell>
                    <TableCell><StatusPill status={t.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>

      <ExpenseDialog expense={selected} open={dlg} onClose={() => setDlg(false)} onChanged={load} />
    </div>
  );
}

const Flow = ({ label, value, sub, tone }) => (
  <div className="text-center">
    <div className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-400">{label}</div>
    <div className={`font-mono tabular-nums text-2xl lg:text-3xl font-semibold mt-1 ${tone}`}>{value}</div>
    <div className="text-xs text-slate-400 mt-0.5">{sub}</div>
  </div>
);

const MiniStat = ({ label, value, sub, tone }) => (
  <Card className="border-border px-4 py-3 min-w-[130px]">
    <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-400">{label}</div>
    <div className={`font-mono tabular-nums text-xl font-semibold mt-0.5 ${tone || "text-slate-900"}`}>{value}</div>
    {sub && <div className="text-[11px] text-slate-400">{sub}</div>}
  </Card>
);
